import React, { useState, useEffect } from "react";
import { getUserCrops, appendToUserCrops, removeFromUserCrops } from "../firebase/firestoreService";
import LoadingPage from "./LoadingPage";

const npkStyle = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%", // Full width of the screen
    height: "100vh", // Full viewport height
    display: "flex",
    justifyContent: "center", // Center horizontally
    alignItems: "center", // Center vertically
    backdropFilter: "blur(3px)", // Subtle background blur
    fontFamily: "'Poppins', sans-serif",
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  video: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: -1,
    filter: "brightness(70%)", // Darken video for better contrast
  },
  formContainer: {
    background: "rgba(255, 255, 255, 0.2)", // Slight transparency for a glass effect
    backdropFilter: "blur(10px)", // Slight blur for glass effect
    padding: "20px", // Unified padding
    borderRadius: "12px", // Smaller border-radius for compactness
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)", // Slightly stronger shadow
    textAlign: "center",
    maxWidth: "950px", // Adjusted max width for better responsiveness
    width: "60%",
    height: "80vh", // Adjust the height to be 80% of the viewport height
    border: "2px solid #f1f8e9",
    display: "flex",
    flexDirection: "column", // Stack the title and crop cards vertically
  },
  h3: {
    fontFamily: "'Roboto Slab', serif",
    fontSize: "1.8rem", // Slightly larger font size
    padding: "12px",
    color: "#FFFFFF",
    fontWeight: "600",
    textShadow: "1px 1px 4px rgba(0, 0, 0, 0.3)",
    flex: "none", // This ensures the header doesn't grow and stays fixed
  },
  cropsWrapper: {
    flex: "1", // This will take the remaining space
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center", // Centers the crop cards within the container
    gap: "12px", // Adds space between the crop cards
    padding: "10px 0", // Padding for vertical spacing
    overflowY: "auto", // Enable vertical scrolling if content overflows
    marginTop: "20px", // Adds some space between the header and the crop cards
  },
  cropCard: {
    padding: "18px 10px",
    border: "2px solid #ccc", // Default border color
    borderRadius: "12px", // Rounded corners
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    minWidth: "90px", // Set a minimum width for the crop cards
    maxWidth: "120px", // Prevent them from becoming too wide
    display: "flex",
    flexDirection: "column", // Stack the icon and name vertically
    alignItems: "center", // Centers the content horizontally
    justifyContent: "center", // Centers the content vertically
    wordWrap: "break-word", // Allow text to wrap
    boxSizing: "border-box",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for card
    position: "relative", // For positioning pseudo-elements if needed
    overflow: "hidden", // Prevent any overflow of content
    background: "linear-gradient(45deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3))", // Glass effect
  },
  selectedCropCard: {
    backgroundColor: "#cce7ff", // Light blue for selected crops
    borderColor: "#4CAF50", // Green border for selected crops
    transform: "scale(1.05)", // Slight scale-up effect when selected
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)", // More pronounced shadow for selected cards
  },
  glowEffect: {
    border: "2px solid #4CAF50", // Glow effect border
    boxShadow: "0 0 10px 5px rgba(76, 175, 80, 0.5)", // Glowing green shadow
  },
  cropIcon: {
    fontSize: "30px", // Larger icon for visibility
  },
  cropName: {
    fontSize: "14px",
    fontWeight: "600",
    marginTop: "8px",
    color: "#333",
    whiteSpace: "normal", // Allow text to wrap
    overflow: "hidden", // Prevent overflow
    wordWrap: "break-word", // Break long words
  },
  loadingText: {
    fontSize: "18px",
    color: "#4CAF50",
    textAlign: "center",
    marginTop: "20px",
  },
};

const cropOptions = {
  paddy: '🌾',         // Rice
  cholam: '🌽',        // Sorghum (Cholam)
  cumbo: '🌾',         // Pearl Millet (Cumbu)
  ragi: '🌾',          // Finger Millet (Ragi)
  maize: '🌽',         // Maize
  redGram: '🌰',       // Red Gram (Pulses)
  bengalGram: '🍛',    // Bengal Gram (Chickpea)
  greenGram: '🍀',     // Green Gram (Moong)
  blackGram: '🌑',     // Black Gram (Urad Dal)
  horseGram: '🐎',     // Horse Gram
  groundnut: '🥜',     // Groundnut (Peanut)
  gingelly: '🌰',      // Sesame (Gingelly)
  potato: '🥔',        // Potato
  onion: '🧅',         // Onion
  sugarcane: '🍬',     // Sugarcane
  cotton: '🌻',        // Cotton
};

const KnownCropsManager = ({ email }) => {
  const [selectedCrops, setSelectedCrops] = useState([]); // User's selected crops
  const [isLoading, setIsLoading] = useState(false); // Loading state

  useEffect(() => {
    const fetchCrops = async () => {
      setIsLoading(true);
      const crops = await getUserCrops(email); // Fetch crops from Firebase
      setSelectedCrops(crops);
      setIsLoading(false);
    };

    fetchCrops();
  }, [email]);

  const handleToggleCrop = async (crop) => {
    let updatedCrops;

    if (selectedCrops.includes(crop)) {
      updatedCrops = selectedCrops.filter((item) => item !== crop);
      await removeFromUserCrops(email, crop); // Remove crop from Firebase
    } else {
      updatedCrops = [...selectedCrops, crop];
      await appendToUserCrops(email, crop); // Add crop to Firebase
    }

    setSelectedCrops(updatedCrops); // Update the local state
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div style={npkStyle.container}>
      <div style={npkStyle.formContainer}>
        <h3 style={npkStyle.h3}>My Known Crops</h3>
        <div style={npkStyle.cropsWrapper}>
          {Object.keys(cropOptions).map((crop) => {
            const isSelected = selectedCrops.includes(crop);
            const cropCardStyle = {
              ...npkStyle.cropCard,
              ...(isSelected ? npkStyle.selectedCropCard : {}),
              ...(isSelected ? npkStyle.glowEffect : {}),
            };

            return (
              <div
                key={crop}
                onClick={() => handleToggleCrop(crop)}
                style={cropCardStyle}
              >
                <div style={npkStyle.cropIcon}>{cropOptions[crop]}</div>
                <div style={npkStyle.cropName}>{crop}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default KnownCropsManager;
